import { Injectable } from '@angular/core';
import {
  combineLatest,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { SplitService } from '@splitsoftware/splitio-angular';
import { EnvironmentService } from '@aptitude/environment';
import { IsAdminService } from './admin';

@Injectable({ providedIn: 'root' })
export class SplitProviderService {
  constructor(
    private splitService: SplitService,
    private environmentService: EnvironmentService,
    private isAdminService: IsAdminService,
  ) {}

  private userName$ = this.isAdminService
    .getAuthToken()
    .tokenPayloadNullable$.pipe(map((payload) => payload.getUserName()));

  private readyClient$ = this.environmentService.environmentData$.pipe(
    switchMap((env) => {
      return this.splitService.init({
        core: {
          authorizationKey: env.splitKey,
          key: 'anonymous',
          labelsEnabled: false,
        },
      });
    }),
    shareReplay(1),
  );

  private readyKeyedClientForCurrentUserName$ = combineLatest([
    this.userName$,
    this.readyClient$,
  ]).pipe(
    switchMap(([userName]) => {
      if (this.splitService.getSDKClient(userName)) {
        return of('');
      }
      return this.splitService.initClient(userName);
    }),
  );

  getTreatment(feature: SplitFeature): Observable<boolean> {
    return this.readyKeyedClientForCurrentUserName$.pipe(
      withLatestFrom(this.userName$),
      map(([_, username]) => this.splitService.getTreatment(username, feature)),
      map(this.isTreatmentOn),
      take(1),
    );
  }

  private isTreatmentOn(treatment: string) {
    return treatment === 'on';
  }
}

export enum SplitFeature {
  AB1020542_SUPPLIER_ENROLLMENT = 'AB1020542_SUPPLIER_ENROLLMENT',
  AB1017913_BID_CALENDAR_LOGIN_DIALOG_SPLIT = 'AB1017913_BID_CALENDAR_LOGIN_DIALOG_SPLIT',
  AB1027465_PRODUCT_ADMINISTRATION_ADMIN_PAGE_SPLIT = 'AB1027465_PRODUCT_ADMINISTRATION_ADMIN_PAGE_SPLIT',
  AB1015029_CREATE_UI_ACCESSIBLE_WORKFLOW_FOR_PARENT_CHILD_ORGANIZATION_MANAGEMENT = 'AB1015029_CREATE_UI_ACCESSIBLE_WORKFLOW_FOR_PARENT_CHILD_ORGANIZATION_MANAGEMENT',
}
